'use client'

import { useLayoutEffect, useState, type RefObject } from 'react'

// Keep in sync with the "filters scroll in one line" breakpoint in
// .bl-fb-controls (styles/blog-listing.css).
const MOBILE_BREAKPOINT = 640

/**
 * Several ancestors clip a position:absolute .bl-ctrl__panel dropdown even
 * though the panel itself never needs to scroll:
 *  - .bl-fb-controls scrolls horizontally on mobile (overflow-x: auto),
 *    which per the CSS Overflow spec also clips overflow-y, but only
 *    below MOBILE_BREAKPOINT where that scroll container exists.
 *  - .bl-post-card has overflow:hidden (to clip cover images to its
 *    rounded corners) unconditionally, at every viewport width.
 * position:fixed escapes both (its containing block is the viewport, not
 * the clipping ancestor), but needs the trigger's live viewport rect
 * computed in JS. Pass always:true for triggers nested in an
 * unconditionally-clipping ancestor like .bl-post-card; otherwise this
 * only kicks in on mobile, where the default CSS positioning works fine
 * everywhere else.
 */
export function useCtrlPanelPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  opts?: { always?: boolean },
) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const always = opts?.always ?? false

  useLayoutEffect(() => {
    if (!open || typeof window === 'undefined' || (!always && window.innerWidth > MOBILE_BREAKPOINT)) {
      setPos(null)
      return
    }
    const update = () => {
      const el = triggerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setPos({ top: rect.bottom + 8, left: rect.left })
    }
    update()
    // Fixed positioning doesn't track the trigger as the page (or any
    // scrollable ancestor) scrolls — recompute on every scroll/resize while
    // open so the panel stays anchored to it instead of drifting behind.
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, triggerRef, always])

  return pos
}
